import type {
  ISchedulerTask,
  ITaskLocation,
  LocationStatus,
  ISiteLocations,
  IScheduleProfession,
} from '@interfaces/IScheduler';
import type { IListItem, IRelatedTask } from '../model/listItem';

export type CellTone = 'complete' | 'at-risk' | 'blocked';

/** Column ordering (no trade sort — trade isn't shown on the tracker). */
export type ColumnSort = 'default' | 'sequence' | 'progress' | 'ontime' | 'critical';
/** Direction for the measured sorts (% complete, on-time risk). */
export type SortDir = 'asc' | 'desc';
/** Which sorts expose an asc/desc toggle (the numeric ones). */
export function sortHasDirection(sort: ColumnSort): boolean {
  return sort === 'progress' || sort === 'ontime';
}

export interface IMatrixCell {
  /** Rolled-up % (floor cells) / status-derived % (area cells, used for the roll-up). */
  percent: number;
  /** Per-area status — set on area (leaf) cells and derived on aggregate (group)
   *  cells; absent on floor roll-up cells (which render by % only). */
  status?: LocationStatus;
  rtlsCoupled: boolean;
}

export interface IMatrixZoneRow {
  zoneId: string;
  zoneName: string;
  cells: Record<string, IMatrixCell | null>;
}

export interface IMatrixFloorRow {
  floorId: string;
  floorName: string;
  cells: Record<string, IMatrixCell | null>;
  zones: IMatrixZoneRow[];
}

/** A predecessor/successor of a column's activity — the subset of the shared
 *  IRelatedTask needed for the header chips (§2-A) + dependency popover (§2-D).
 *  Resolved against the whole plan, so an uncommitted neighbour is listed too
 *  (`committed: false`) — it is still upstream work, it just has no column. */
export type IColumnDepRef = Pick<IRelatedTask, 'id' | 'name' | 'progressPercent' | 'committed'>;

export interface IMatrixColumn {
  /** Task id, or group id when `isGroup` (Generation 1 = phase/group columns). */
  taskId: string;
  name: string;
  profession?: IScheduleProfession;
  progressPercent: number;
  isGroup?: boolean;
  /** True for a milestone task — shown with a diamond marker beside the %. Group
   *  columns are never milestones. */
  isMilestone?: boolean;
  /** CPM critical-path membership — task columns only (a group is "critical" if
   *  any of its tasks are). Drives the gold header tag + the criticalPath filter. */
  isCritical?: boolean;
  /** On-time risk 0–5 (same score as the Daily List). Group = worst child. Sort key. */
  onTimeRisk?: number;
  /** Upstream/downstream activities (empty for group columns). */
  predecessors?: IColumnDepRef[];
  successors?: IColumnDepRef[];
}

export interface IProgressMatrix {
  columns: IMatrixColumn[];
  floors: IMatrixFloorRow[];
  maxGeneration: number;
  buildingName?: string;
}

/** What an area's status is worth when it carries no explicit percent — read
 *  over Trusstor's per-area vocabulary (2026-09-17 task-commit design). */
const STATUS_PERCENT: Record<LocationStatus, number> = {
  unplanned: 0,
  planned: 0,
  inProgress: 50,
  delayed: 50,
  delayedOverdue: 50,
  overdue: 50,
  readyForReview: 90,
  complete: 100,
};

export function locationPercent(loc: ITaskLocation): number {
  return loc.progressPercent ?? STATUS_PERCENT[loc.status];
}

/** Depth (0-based) of each task via its parentId chain. Cycle-safe. */
export function computeTaskDepth(tasks: readonly ISchedulerTask[]): Map<string, number> {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const depth = new Map<string, number>();
  for (const t of tasks) {
    let d = 0;
    let cur: ISchedulerTask | undefined = t;
    const seen = new Set<string>();
    while (cur?.parentId && byId.has(cur.parentId) && !seen.has(cur.parentId)) {
      seen.add(cur.parentId);
      d += 1;
      cur = byId.get(cur.parentId);
    }
    depth.set(t.id, d);
  }
  return depth;
}

function buildCell(loc: ITaskLocation): IMatrixCell {
  return { percent: locationPercent(loc), status: loc.status, rtlsCoupled: !!loc.rtlsCoupled };
}

function rollupFloorCell(
  zoneCells: Array<IMatrixCell | null>,
  deriveStatus = false,
): IMatrixCell | null {
  const present = zoneCells.filter((c): c is IMatrixCell => c != null);
  if (present.length === 0) return null;
  const percent = Math.round(present.reduce((s, c) => s + c.percent, 0) / present.length);
  const rtlsCoupled = present.some((c) => c.rtlsCoupled);
  // Floor cells render by % (no status needed). But an AGGREGATE column (a
  // Generation-1 phase/group) renders its per-zone cell as an area glyph, which
  // needs a status — and a rollup has no single one. Derive it from the blend so
  // the glyph agrees with the rolled-up % instead of always falling back to the
  // "planned" ring (a 100% zone was showing an unplanned ring): any child late
  // → delayed, else 100% → complete, >0 → in progress, else planned.
  if (deriveStatus) {
    const late = new Set<LocationStatus>(['delayed', 'delayedOverdue', 'overdue']);
    const status: LocationStatus = present.some((c) => c.status !== undefined && late.has(c.status))
      ? 'delayed'
      : percent >= 100 ? 'complete' : percent > 0 ? 'inProgress' : 'planned';
    return { percent, status, rtlsCoupled };
  }
  return { percent, rtlsCoupled };
}

export interface IMatrixGroup {
  id: string;
  name: string;
}

/**
 * Stable topological order over the columns' own dependency edges (predecessor
 * before successor), restricted to the present columns. Kahn's algorithm, ties
 * broken by original index; any cycle remainder is appended in original order.
 */
function topoSortColumns(columns: IMatrixColumn[]): IMatrixColumn[] {
  const index = new Map(columns.map((c, i) => [c.taskId, i] as const));
  const present = new Set(columns.map((c) => c.taskId));
  const indeg = new Map<string, number>();
  const succ = new Map<string, string[]>();
  for (const c of columns) {
    const preds = (c.predecessors ?? []).filter((p) => present.has(p.id));
    indeg.set(c.taskId, preds.length);
    for (const p of preds) (succ.get(p.id) ?? succ.set(p.id, []).get(p.id)!).push(c.taskId);
  }
  const byOriginal = (a: string, b: string) => (index.get(a) ?? 0) - (index.get(b) ?? 0);
  const ready = columns.filter((c) => (indeg.get(c.taskId) ?? 0) === 0).map((c) => c.taskId);
  const out: string[] = [];
  const seen = new Set<string>();
  while (ready.length) {
    ready.sort(byOriginal);
    const id = ready.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    for (const s of succ.get(id) ?? []) {
      indeg.set(s, (indeg.get(s) ?? 0) - 1);
      if ((indeg.get(s) ?? 0) === 0) ready.push(s);
    }
  }
  // Cycle remainder (never reached indeg 0) — keep original order.
  for (const c of columns) if (!seen.has(c.taskId)) out.push(c.taskId);
  const byId = new Map(columns.map((c) => [c.taskId, c] as const));
  return out.map((id) => byId.get(id)!);
}

/** Reorder columns per the chosen sort + direction (cells are keyed by id, so
 *  order is all that changes). Array#sort is stable, preserving the built order
 *  within ties. `dir` only applies to the measured sorts (progress, on-time);
 *  sequence and critical keep their inherent order. */
export function sortColumns(columns: IMatrixColumn[], sort: ColumnSort, dir: SortDir = 'desc'): IMatrixColumn[] {
  const sign = dir === 'asc' ? -1 : 1; // desc = high value first
  switch (sort) {
    case 'sequence':
      return topoSortColumns(columns);
    case 'progress':
      return [...columns].sort((a, b) => sign * (b.progressPercent - a.progressPercent));
    case 'ontime':
      return [...columns].sort((a, b) => sign * ((b.onTimeRisk ?? 0) - (a.onTimeRisk ?? 0)));
    case 'critical':
      return [...columns].sort((a, b) => Number(!!b.isCritical) - Number(!!a.isCritical));
    default:
      return columns;
  }
}

/** A neighbour as a column header lists it: the shared IRelatedTask's name,
 *  live % and whether it is committed (an uncommitted one has no column). */
function toDepRef({ id, name, progressPercent, committed }: IRelatedTask): IColumnDepRef {
  return { id, name, progressPercent, committed };
}

/**
 * Generation model (matches the scheduler's WBS levels):
 *   Generation 1 = groups/phases (rolled up over their tasks),
 *   Generation 2 = top-level tasks (parentId-depth 0),
 *   Generation g≥2 = tasks at parentId-depth (g-2).
 * So a schedule of phase-groups + flat tasks yields 2 generations.
 *
 * The builder lays out; it does not choose. Its columns are `shown` — the
 * tracker's activities, chosen by the page's one "in view" selector
 * (`trackerActivities`) the header counts — and each carries the list item's
 * critical flag, on-time risk and neighbours, so the tracker and the Daily
 * List can never score or relate an activity apart.
 */
export function buildProgressMatrix(params: {
  /** The whole feed — the parentId chains each shown activity's level reads,
   *  whatever the filters leave. */
  tasks: readonly ISchedulerTask[];
  /** The tracker's activities, every one with areas (`trackerActivities`). */
  shown: readonly IListItem[];
  groups?: IMatrixGroup[];
  locations: ISiteLocations;
  generation: number;
  /** Column ordering. */
  sort?: ColumnSort;
  /** Sort direction for the measured sorts. */
  dir?: SortDir;
}): IProgressMatrix {
  const { tasks, shown, groups = [], locations, generation, sort = 'default', dir = 'desc' } = params;
  const depth = computeTaskDepth(tasks);
  const itemOf = new Map(shown.map((item) => [item.raw.activity!.id, item] as const));
  const located = shown.map((item) => item.raw.activity!);
  const isCritical = (t: ISchedulerTask): boolean => itemOf.get(t.id)!.isOnCriticalPath;
  const riskOf = (t: ISchedulerTask): number => itemOf.get(t.id)!.onTimeRisk.level;

  const maxTaskDepth = located.reduce((m, t) => Math.max(m, depth.get(t.id) ?? 0), 0);
  const maxGeneration = located.length > 0 ? maxTaskDepth + 2 : 1;
  // Clamp a requested level into range: a restored level (localStorage) can
  // exceed THIS schedule's depth (a different / shallower schedule), so show the
  // deepest available level rather than an empty grid.
  const gen = Math.min(Math.max(1, generation), maxGeneration);

  // Per located task: zoneId → cell.
  const taskZoneCell = new Map<string, Map<string, IMatrixCell>>();
  for (const t of located) {
    const m = new Map<string, IMatrixCell>();
    for (const loc of t.locations ?? []) m.set(loc.locationId, buildCell(loc));
    taskZoneCell.set(t.id, m);
  }

  let columns: IMatrixColumn[];

  if (gen === 1) {
    // Group columns: aggregate each phase's located tasks.
    const groupName = new Map(groups.map((g) => [g.id, g.name]));
    const byGroup = new Map<string, ISchedulerTask[]>();
    for (const t of located) {
      const arr = byGroup.get(t.groupId) ?? [];
      arr.push(t);
      byGroup.set(t.groupId, arr);
    }
    columns = [...byGroup.entries()].map(([groupId, ts]) => ({
      taskId: groupId,
      name: groupName.get(groupId) ?? groupId,
      progressPercent: Math.round(ts.reduce((s, t) => s + t.progressPercent, 0) / ts.length),
      isGroup: true,
      // A phase sits on the critical path if any of its tasks do, and takes its
      // worst child's on-time risk.
      isCritical: ts.some(isCritical),
      onTimeRisk: ts.reduce((m, t) => Math.max(m, riskOf(t)), 0),
    }));
  } else {
    const cols = located.filter((t) => (depth.get(t.id) ?? 0) === gen - 2);
    columns = cols.map((t) => {
      const related = itemOf.get(t.id)!.relatedTasks;
      return {
        taskId: t.id,
        name: t.name,
        profession: t.profession,
        progressPercent: t.progressPercent,
        isMilestone: t.isMilestone,
        isCritical: isCritical(t),
        onTimeRisk: riskOf(t),
        predecessors: (related?.predecessors ?? []).map(toDepRef),
        successors: (related?.successors ?? []).map(toDepRef),
      };
    });
  }

  columns = sortColumns(columns, sort, dir);

  // SPARSE cell construction — O(location-assignments), not O(zones × columns).
  // Iterate each located task's assignments once and write only the cells that
  // exist; a missing key reads as null downstream (`cells[id] ?? null`). At
  // Generation 1 a column is a GROUP, so multiple tasks can land in the same
  // (zone, group) cell — collect per-task cells first, then roll each bucket up
  // with a derived status (same status-derivation rollupFloorCell applies).
  const colIds = new Set(columns.map((c) => c.taskId));
  const groupOfTask = gen === 1 ? new Map(located.map((t) => [t.id, t.groupId] as const)) : null;
  // zoneId → columnId → per-task cells contributing to that grid cell.
  const contrib = new Map<string, Map<string, IMatrixCell[]>>();
  for (const [taskId, zoneCells] of taskZoneCell) {
    const columnId = groupOfTask ? groupOfTask.get(taskId) : taskId;
    if (!columnId || !colIds.has(columnId)) continue;
    for (const [zoneId, cell] of zoneCells) {
      const byCol = contrib.get(zoneId) ?? contrib.set(zoneId, new Map()).get(zoneId)!;
      (byCol.get(columnId) ?? byCol.set(columnId, []).get(columnId)!).push(cell);
    }
  }

  const floors: IMatrixFloorRow[] = locations.floors.map((floor) => {
    // Floor rollup: bucket each zone's present cells per column, then average.
    const floorContrib = new Map<string, IMatrixCell[]>();
    const zones: IMatrixZoneRow[] = floor.zones.map((zone) => {
      const cells: Record<string, IMatrixCell | null> = {};
      for (const [columnId, cs] of contrib.get(zone.id) ?? []) {
        // gen ≥ 2: the bucket always has exactly one cell — the per-task Map
        // in taskZoneCell already deduped by locationId, and columnId === taskId.
        const cell = gen === 1 ? rollupFloorCell(cs, true) : cs[0]!;
        cells[columnId] = cell;
        if (cell) {
          (floorContrib.get(columnId) ?? floorContrib.set(columnId, []).get(columnId)!).push(cell);
        }
      }
      return { zoneId: zone.id, zoneName: zone.name, cells };
    });
    const cells: Record<string, IMatrixCell | null> = {};
    for (const [columnId, cs] of floorContrib) cells[columnId] = rollupFloorCell(cs);
    return { floorId: floor.id, floorName: floor.name, cells, zones };
  });

  return { columns, floors, maxGeneration, buildingName: locations.buildingName };
}
